'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import * as React from 'react';
import { useControlledLatch } from '../../../lib/controlled-state';
import { developmentDiagnosticsEnabled } from '../../../lib/diagnostics';
import { createRenderIdentityPlan } from '../../../lib/render-identities';
import { matchesSearchTerms } from '../../../lib/search';
import { cn } from '../../../lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { SpecraCommandResult } from '../lieutenants/command-result';
const defaultKeyboardShortcut = {
    altKey: false,
    key: 'k',
    metaOrCtrl: true,
    shiftKey: false
};
const filterSearchText = (_internalIdentity, search, keywords = [])=>{
    // cmdk's value is the stable item id. Search stays on the user-facing
    // label/description/keywords assembled below so opaque ids never leak into results.
    return matchesSearchTerms(search, keywords);
};
/** Search and command surface extracted from the Delivery global navigation. */ export function SpecraCommandPalette({ allowShortcutInEditable = true, closeOnSelect = true, contentProps, defaultOpen = false, defaultQuery = '', description = 'Search across the current workspace.', emptyText = 'No results found.', footer, getItemProps, getItemTestId, inputProps, items, keyboardShortcut = defaultKeyboardShortcut, loading = false, loadingText = 'Searching…', onOpenChange, open, onQueryChange, placeholder = 'Search programs, records, and actions…', query, scopeLabel, showFooter = true, shouldFilter = true, shouldHandleShortcut, title = 'Search' }) {
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
    const [internalQuery, setInternalQuery] = React.useState(defaultQuery);
    const openControlled = useControlledLatch(open !== undefined);
    const queryControlled = useControlledLatch(query !== undefined);
    const resolvedOpen = openControlled ? open ?? false : internalOpen;
    const resolvedQuery = queryControlled ? query ?? '' : internalQuery;
    const updateOpen = React.useCallback((nextOpen)=>{
        if (!openControlled) setInternalOpen(nextOpen);
        if (!nextOpen) {
            if (!queryControlled) setInternalQuery('');
            onQueryChange?.('');
        }
        onOpenChange?.(nextOpen);
    }, [
        onOpenChange,
        onQueryChange,
        openControlled,
        queryControlled
    ]);
    const shortcutStateRef = React.useRef({
        allowShortcutInEditable,
        keyboardShortcut,
        resolvedOpen,
        shouldHandleShortcut,
        updateOpen
    });
    React.useEffect(()=>{
        shortcutStateRef.current = {
            allowShortcutInEditable,
            keyboardShortcut,
            resolvedOpen,
            shouldHandleShortcut,
            updateOpen
        };
    });
    const updateQuery = (nextQuery)=>{
        if (!queryControlled) setInternalQuery(nextQuery);
        onQueryChange?.(nextQuery);
    };
    React.useEffect(()=>{
        const handleKeyDown = (event)=>{
            const shortcutState = shortcutStateRef.current;
            if (event.defaultPrevented) return;
            if (event.repeat) return;
            if (!shortcutState.keyboardShortcut) return;
            const keyMatches = event.key.toLowerCase() === shortcutState.keyboardShortcut.key.toLowerCase();
            const modifierMatches = shortcutState.keyboardShortcut.metaOrCtrl === false ? !event.metaKey && !event.ctrlKey : event.metaKey || event.ctrlKey;
            const altMatches = shortcutState.keyboardShortcut.altKey === undefined || event.altKey === shortcutState.keyboardShortcut.altKey;
            const shiftMatches = shortcutState.keyboardShortcut.shiftKey === undefined || event.shiftKey === shortcutState.keyboardShortcut.shiftKey;
            if (!keyMatches || !modifierMatches || !altMatches || !shiftMatches) return;
            const target = event.target;
            const editable = target instanceof Element && Boolean(target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"], [role="searchbox"], [role="combobox"]'));
            if (!shortcutState.allowShortcutInEditable && editable || shortcutState.shouldHandleShortcut && shortcutState.shouldHandleShortcut(event) !== true) return;
            event.preventDefault();
            shortcutState.updateOpen(!shortcutState.resolvedOpen);
        };
        window.addEventListener('keydown', handleKeyDown);
        return ()=>window.removeEventListener('keydown', handleKeyDown);
    }, []);
    const identityPlan = React.useMemo(()=>createRenderIdentityPlan(items.map((item)=>item.id), (id)=>id.trim().toLocaleLowerCase()), [
        items
    ]);
    const duplicateItemIdSignature = developmentDiagnosticsEnabled ? identityPlan.duplicateIds.join('\u0000') : '';
    React.useEffect(()=>{
        if (!developmentDiagnosticsEnabled || !duplicateItemIdSignature) return;
        console.error(`SpecraCommandPalette received duplicate item ids: ${identityPlan.duplicateIds.join(', ')}. Ids must be unique; render-only fallback identities were applied.`);
    }, [
        duplicateItemIdSignature,
        identityPlan.duplicateIds
    ]);
    const groups = React.useMemo(()=>{
        const nextGroups = new Map();
        for (const [index, item] of items.entries()){
            const group = item.group ?? 'Results';
            const groupItems = nextGroups.get(group);
            const renderedItem = {
                item,
                renderIdentity: identityPlan.identities[index]
            };
            if (groupItems) groupItems.push(renderedItem);
            else nextGroups.set(group, [
                renderedItem
            ]);
        }
        return nextGroups;
    }, [
        identityPlan.identities,
        items
    ]);
    return /*#__PURE__*/ _jsx(Dialog, {
        onOpenChange: updateOpen,
        open: resolvedOpen,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            ...contentProps,
            className: cn(// Top-anchored (mockup: fixed near the top), not vertically centered.
            // The base DialogContent applies a vertical-centering transform;
            // neutralize it with translate-y-0 so a tall result list hangs
            // downward instead of overflowing above the viewport and clipping
            // the search input.
            'top-[var(--component-command-top-mobile)] translate-y-0 w-[calc(100%-var(--component-overlay-edge-gap))] gap-0 overflow-hidden p-0 [&_[cmdk-input-wrapper]]:pr-[var(--space-10)] sm:top-[var(--component-command-top-desktop)] sm:max-w-xl', contentProps?.className),
            children: [
                /*#__PURE__*/ _jsx(DialogTitle, {
                    className: "sr-only",
                    children: title
                }),
                /*#__PURE__*/ _jsx(DialogDescription, {
                    className: "sr-only",
                    children: description
                }),
                /*#__PURE__*/ _jsxs(Command, {
                    "aria-busy": loading || undefined,
                    className: "bg-card",
                    filter: filterSearchText,
                    shouldFilter: shouldFilter,
                    children: [
                        /*#__PURE__*/ _jsx(CommandInput, {
                            ...inputProps,
                            onValueChange: updateQuery,
                            placeholder: placeholder,
                            value: resolvedQuery
                        }),
                        /*#__PURE__*/ _jsx("output", {
                            className: cn(loading ? 'block py-[var(--space-6)] text-center text-[length:var(--fs-ui-caption)] text-[color:var(--text-muted)]' : 'sr-only'),
                            children: loading ? loadingText : ''
                        }),
                        /*#__PURE__*/ _jsxs(CommandList, {
                            className: "max-h-[var(--component-command-results-max-height)]",
                            children: [
                                !loading ? /*#__PURE__*/ _jsx(CommandEmpty, {
                                    children: emptyText
                                }) : null,
                                [
                                    ...groups
                                ].map(([group, groupItems])=>/*#__PURE__*/ _jsx(CommandGroup, {
                                        heading: group,
                                        children: groupItems.map(({ item, renderIdentity })=>{
                                            const resolvedItemProps = getItemProps?.(item);
                                            return /*#__PURE__*/ _createElement(CommandItem, {
                                                ...item.itemProps,
                                                ...resolvedItemProps,
                                                className: cn('gap-3 py-2.5', item.itemProps?.className, resolvedItemProps?.className),
                                                "data-command-id": item.id,
                                                "data-testid": getItemTestId?.(item) ?? item.testId ?? resolvedItemProps?.['data-testid'] ?? item.itemProps?.['data-testid'],
                                                disabled: item.disabled,
                                                key: renderIdentity,
                                                keywords: [
                                                    item.label,
                                                    ...item.description ? [
                                                        item.description
                                                    ] : [],
                                                    ...item.keywords ?? []
                                                ],
                                                onSelect: ()=>{
                                                    item.onSelect();
                                                    if (closeOnSelect) updateOpen(false);
                                                },
                                                value: renderIdentity
                                            }, /*#__PURE__*/ _jsx(SpecraCommandResult, {
                                                description: item.description,
                                                icon: item.icon,
                                                label: item.label,
                                                meta: item.meta,
                                                tone: item.tone
                                            }));
                                        })
                                    }, group))
                            ]
                        }),
                        showFooter ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-1)] border-t border-[color:var(--border-subtle)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--fs-ui-micro)] text-[color:var(--text-ghost)]",
                            children: [
                                footer ?? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                /*#__PURE__*/ _jsx("strong", {
                                                    className: "font-medium text-[color:var(--text-faint)]",
                                                    children: "↑↓"
                                                }),
                                                ' ',
                                                "navigate"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                /*#__PURE__*/ _jsx("strong", {
                                                    className: "font-medium text-[color:var(--text-faint)]",
                                                    children: "↵"
                                                }),
                                                " open"
                                            ]
                                        })
                                    ]
                                }),
                                scopeLabel ? /*#__PURE__*/ _jsx("span", {
                                    className: "ml-auto",
                                    children: scopeLabel
                                }) : null
                            ]
                        }) : null
                    ]
                })
            ]
        })
    });
}
